import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Delay (ms) before opening a submenu on pointer hover — reduces accidental opens on desktop. */
const POINTER_ACTIVATE_DELAY = 150;
const DEACTIVATE_DELAY = 350;

const ACTIVATION_TEXT_SELECTOR = '.menu-list__link-title';

/** @type {boolean} */
let pageShowListenerBound = false;

function isFinePointerDevice() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isActivationTextTarget(element) {
  if (!(element instanceof Element)) return false;
  return element.matches(ACTIVATION_TEXT_SELECTOR) || !!element.closest(ACTIVATION_TEXT_SELECTOR);
}

/**
 * @param {HTMLElement} menuItem
 * @returns {HTMLElement | null}
 */
function getActivationTextElement(menuItem) {
  const title = menuItem.querySelector(ACTIVATION_TEXT_SELECTOR);
  return title instanceof HTMLElement ? title : null;
}

/**
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isActivationTextHovered(menuItem) {
  const title = getActivationTextElement(menuItem);
  return title?.matches(':hover') ?? false;
}

/**
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isSubmenuHovered(menuItem) {
  const listItem = menuItem.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('.menu-list__submenu');
  return submenu?.matches(':hover') ?? false;
}

/**
 * @param {PointerEvent} event
 * @param {HTMLElement} menuItem
 * @returns {boolean}
 */
function isPointerOverActivationText(event, menuItem) {
  const title = getActivationTextElement(menuItem);
  if (!title) return false;

  if (title.matches(':hover')) return true;

  if (event.clientX === 0 && event.clientY === 0) {
    return title.matches(':hover');
  }

  const hit = document.elementFromPoint(event.clientX, event.clientY);
  return hit instanceof Element && (hit === title || title.contains(hit));
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#bindPageShowListener();
    this.#resetMenuState();
    this.#bindOverflowReflowGuard();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#resetMenuStateIfNotIntentionallyOpen();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#resetMenuState();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * True when another desktop menu in the same header already has a submenu open.
   * @returns {boolean}
   */
  #anySiblingHeaderMenuExpanded() {
    const root = this.closest('header-component');
    if (!root) return false;
    for (const el of root.querySelectorAll('header-menu')) {
      if (el === this) continue;
      if (el.getAttribute('aria-expanded') === 'true') return true;
    }
    return false;
  }

  /**
   * Resets open submenu state (e.g. after navigation or bfcache restore).
   */
  reset() {
    this.#resetMenuState();
  }

  /**
   * Keeps the submenu open while the pointer moves from the activation text into the panel.
   */
  holdOpen = () => {
    this.#debouncedDeactivate.cancel();
  };

  #bindPageShowListener() {
    if (pageShowListenerBound) return;
    pageShowListenerBound = true;

    window.addEventListener('pageshow', () => {
      document.querySelectorAll('header-menu').forEach((menu) => {
        if (menu instanceof HeaderMenu) menu.reset();
      });
    });
  }

  #bindOverflowReflowGuard() {
    const overflowList = this.refs.overflowMenu;
    if (!(overflowList instanceof HTMLElement)) return;

    const observer = new ResizeObserver(() => {
      this.#resetMenuStateIfNotIntentionallyOpen();
    });

    observer.observe(overflowList);
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  #resetMenuStateIfNotIntentionallyOpen() {
    const activeItem = this.#state.activeItem;
    if (!(activeItem instanceof HTMLElement)) {
      this.#resetMenuState();
      return;
    }

    if (!isActivationTextHovered(activeItem) && !isSubmenuHovered(activeItem) && !this.overflowHovered) {
      this.#resetMenuState();
    }
  }

  #resetMenuState() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    const item = this.#state.activeItem;
    this.#state.activeItem = null;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    this.removeAttribute('data-animating');

    if (item instanceof HTMLElement) {
      item.ariaExpanded = 'false';
      item.removeAttribute('data-animating');
    }

    this.querySelectorAll('[ref="menuitem"][aria-expanded="true"]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.ariaExpanded = 'false';
        el.removeAttribute('data-animating');
      }
    });

    this.querySelectorAll('.menu-list__submenu').forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        submenu.setAttribute('hidden', '');
        submenu.setAttribute('inert', '');
      }
    });
  }

  /**
   * Opens the submenu only when the pointer enters the activation text.
   * @param {PointerEvent} event
   */
  activate = (event) => {
    if (!(event instanceof PointerEvent) || !event.isTrusted || !isFinePointerDevice()) return;
    if (!(event.target instanceof Element) || !isActivationTextTarget(event.target)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    const skipPointerDelay =
      this.#state.activeItem != null || this.#anySiblingHeaderMenuExpanded();

    if (skipPointerDelay) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    if (item.getAttribute('aria-haspopup') !== 'true') return;

    if (!isPointerOverActivationText(event, item)) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const listItem = item.closest('.menu-list__list-item');
    const slotName = listItem?.getAttribute('slot') ?? '';
    this.dataset.overflowExpanded = String(slotName === 'overflow' || slotName === 'more');

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenu instanceof HTMLElement) {
      submenu.removeAttribute('hidden');
      submenu.removeAttribute('inert');
      this.#promoteLazyImagesIn(submenu);
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, POINTER_ACTIVATE_DELAY);

  /**
   * @param {PointerEvent} event
   */
  deactivate(event) {
    if (!(event instanceof PointerEvent)) return;

    this.#debouncedActivateHandler.cancel();

    if (!this.#state.activeItem) return;

    const item =
      event.target instanceof Element ? findMenuItem(event.target) ?? this.#state.activeItem : this.#state.activeItem;

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;
    if (isSubmenuHovered(item)) return;
    if (isActivationTextHovered(item)) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const submenu = findSubmenu(item);
    if (submenu instanceof HTMLElement) {
      submenu.removeAttribute('hidden');
      submenu.removeAttribute('inert');
    }

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu instanceof HTMLElement) {
        submenu.setAttribute('hidden', '');
        submenu.setAttribute('inert', '');
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Start loading lazy images inside a submenu only after it opens (better initial page weight).
   * @param {HTMLElement} root
   */
  #promoteLazyImagesIn(root) {
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.removeAttribute('loading');
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreListItem = element.closest('[slot="more"]');
  if (moreListItem instanceof HTMLElement) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreListItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const ancestor = element.closest('[ref="menuitem"]');
  if (ancestor instanceof HTMLElement) return ancestor;

  const descendant = element.querySelector('[ref="menuitem"]');
  return descendant instanceof HTMLElement ? descendant : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
